import olMap from 'ol/Map';
import { Overlay } from 'ol';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import OverlayPositioning from 'ol/OverlayPositioning';
import { escapeHtml } from './MapGpxUtils';

/**
 * Popover shown when a waypoint marker is clicked outside an edit session.
 * Rendered as an OL Overlay inside #map so it works in full-screen mode.
 */
export class GpxWaypointPopover {

    private element: HTMLDivElement;
    private overlay: Overlay;
    private autoCloseTimer: number | null = null;

    public constructor(private map: olMap) {
        this.element = document.createElement('div');
        this.element.className = 'gpx-waypoint-popover';
        this.element.style.display = 'none';

        this.overlay = new Overlay({
            element: this.element,
            positioning: OverlayPositioning.BOTTOM_CENTER,
            offset: [0, -44],
            autoPan: true,
            autoPanMargin: 40
        });
        this.map.addOverlay(this.overlay);
    }

    public show = (feature: Feature): void => {
        const name = <string>feature.get('name') ?? '';
        const description = <string | null>feature.get('description');

        this.element.innerHTML = `
            <div class="gpx-waypoint-popover-inner">
                <button type="button" class="btn-close gpx-waypoint-popover-close" aria-label="Close"></button>
                <div class="fw-bold small">${escapeHtml(name)}</div>
                ${description ? `<div class="small mt-1">${escapeHtml(description).replace(/\r?\n/g, '<br>')}</div>` : ''}
            </div>`;

        this.element.querySelector('.gpx-waypoint-popover-close')
            ?.addEventListener('click', () => this.hide());

        // Make the element visible BEFORE positioning: the overlay's autoPan
        // measures the element on setPosition, and a display:none element reports
        // zero size at the page origin — which makes autoPan think the popover is
        // off-screen and pan the map on every click even when there's room.
        this.element.style.display = 'block';
        const geometry = <Point>feature.getGeometry();
        this.overlay.setPosition(geometry.getCoordinates());

        if (this.autoCloseTimer !== null) {
            window.clearTimeout(this.autoCloseTimer);
        }
        this.autoCloseTimer = window.setTimeout(() => this.hide(), 6000);
    }

    public hide = (): void => {
        this.element.style.display = 'none';
        this.overlay.setPosition(undefined);
        if (this.autoCloseTimer !== null) {
            window.clearTimeout(this.autoCloseTimer);
            this.autoCloseTimer = null;
        }
    }
}
